import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MovdMarketingHero from '../design-system/MovdMarketingHero.jsx';
import MovdInput from '../design-system/MovdInput.jsx';
import MovdButton from '../design-system/MovdButton.jsx';
import MovdNotice from '../design-system/MovdNotice.jsx';
import { movdTokens } from '../design-system/movdTokens.js';

const cardStyle = { padding: 24, borderRadius: 12, boxShadow: movdTokens.shadow.raised, background: '#fff' };

const contactMethods = [
  { icon: '\u260E', title: 'Phone support', details: '1-800-MOVD-123', href: 'tel:+18006683123', description: '24/7 customer support' },
  { icon: '\u2709', title: 'Email support', details: 'support@movd.ca', href: 'mailto:support@movd.ca', description: 'Response within 2 hours' },
  { icon: '\uD83D\uDCCD', title: 'Head office', details: 'Toronto, ON', href: null, description: 'Serving all of Canada' },
  { icon: '\uD83D\uDD52', title: 'Response time', details: 'Within 2 hours', href: null, description: 'Mon – Sun, 8 am – 10 pm ET' },
];

export default function ContactPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: 'General', message: '' });
  const [notice, setNotice] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async e => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setNotice({ severity: 'error', text: 'Please fill in all required fields' });
      return;
    }
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 900));
    setSubmitting(false);
    setNotice({ severity: 'success', text: "Thank you for your message! We'll get back to you within 24 hours." });
    setFormData({ name: '', email: '', phone: '', subject: 'General', message: '' });
  };

  return (
    <div style={{ minHeight: '100vh' }}>
      <MovdMarketingHero eyebrow="Get in touch" title="Contact us" subtitle="We're here to help with all your moving needs — reach out any time." />

      <div style={{ maxWidth: 1120, margin: '0 auto', padding: 'clamp(24px, 5vw, 48px) 20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20, marginBottom: 48 }}>
          {contactMethods.map(method => (
            <div key={method.title} style={{ ...cardStyle, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }} aria-hidden="true">{method.icon}</div>
              <h3 style={{ margin: '0 0 8px', fontWeight: 700, fontSize: 17 }}>{method.title}</h3>
              {method.href ? <a href={method.href} style={{ display: 'block', marginBottom: 4, fontWeight: 700, color: movdTokens.color.action.primary }}>{method.details}</a> : <p style={{ margin: '0 0 4px', fontWeight: 700 }}>{method.details}</p>}
              <p style={{ margin: 0, color: movdTokens.color.content.secondary, fontSize: 13 }}>{method.description}</p>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(240px, 1fr)', gap: 32, alignItems: 'start' }}>
          <div style={cardStyle}>
            <h2 style={{ margin: '0 0 20px', fontWeight: 800, fontSize: 20 }}>Send us a message</h2>
            <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <MovdInput label="Full name" name="name" value={formData.name} onChange={handleInputChange} required />
                <MovdInput label="Email address" name="email" type="email" value={formData.email} onChange={handleInputChange} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <MovdInput label="Phone number" name="phone" value={formData.phone} onChange={handleInputChange} />
                <label style={{ display: 'grid', gap: 8, color: movdTokens.color.tealSoft, fontSize: 11, fontWeight: 900, letterSpacing: '.14em', textTransform: 'uppercase' }}>
                  <span>Subject</span>
                  <select name="subject" value={formData.subject} onChange={handleInputChange} style={{ minHeight: 52, borderRadius: movdTokens.radius.control, border: '1px solid rgba(0,0,0,.15)', padding: '0 12px', font: 'inherit', fontSize: 15, fontWeight: 600, textTransform: 'none', color: movdTokens.color.ink }}>
                    <option value="General">General inquiry</option>
                    <option value="Booking Help">Booking help</option>
                    <option value="Partner Inquiry">Partner inquiry</option>
                    <option value="Complaint">Complaint</option>
                  </select>
                </label>
              </div>
              <label style={{ display: 'grid', gap: 8, color: movdTokens.color.tealSoft, fontSize: 11, fontWeight: 900, letterSpacing: '.14em', textTransform: 'uppercase' }}>
                <span>Message</span>
                <textarea name="message" value={formData.message} onChange={handleInputChange} required rows={4} placeholder="Tell us how we can help you..." style={{ borderRadius: movdTokens.radius.control, border: '1px solid rgba(0,0,0,.15)', padding: 12, font: 'inherit', fontSize: 15, fontWeight: 600, textTransform: 'none', color: movdTokens.color.ink, resize: 'vertical' }} />
              </label>
              <MovdButton type="submit" loading={submitting}>Send message</MovdButton>
            </form>
          </div>

          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 12px', fontWeight: 700, fontSize: 17 }}>Need a quote instead?</h3>
            <p style={{ margin: '0 0 16px', color: movdTokens.color.content.secondary, fontSize: 14, lineHeight: 1.6 }}>Looking to book a move? Get instant quotes from verified movers in your area and start planning your move today.</p>
            <MovdButton onClick={() => navigate('/')} style={{ width: '100%' }}>Get free quote</MovdButton>
          </div>
        </div>
      </div>

      {notice && <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1500, minWidth: 280 }}><MovdNotice tone={notice.severity}>{notice.text}</MovdNotice></div>}
    </div>
  );
}
